/* eslint-disable camelcase */
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';

import { GenericForm, ConfirmationModal } from '@components';
import Button from '@components/Forms/Button';
import InputText from '@components/Inputs/InputText';
import Select from '@components/Forms/Select';
import { useBankAccount, useCommon } from '@hooks';
import { capitalize } from '@helpers';

import { bankAccountTypeColombia, saparateDocSelected } from '@constants/bankAccountType';
import { useSelector } from 'react-redux';
import filter from 'lodash.filter';
import styled from 'styled-components';

import classes from './FormBankAccount.module.scss';
import schemaCo from './schemaCo';

const TextNote = styled.h4`
  font-family: Roboto;
  font-size: 14px;
  line-height: 16px;
  padding: 16px;
  border-radius: 6px;
  background: #c0dbf3;
  color: #0a5aa2 !important;
`;

const FormBankAccountCo = ({ edit, docs }) => {
  const { business_name, legal_agent } = useSelector((state) => state.login.merchant);
  const { t } = useTranslation();
  const history = useHistory();
  const { setModal } = useCommon();
  const {
    bankAccount: initialBankAccount,
    createBankAccount,
    updateBankAccount,
    banksCodes,
  } = useBankAccount();
  const [bankAccount, setBankAccount] = useState(edit ? initialBankAccount : {});
  const [anyChange, setChange] = useState(false);

  const [docSelected, setDocSelected] = useState(
    edit && initialBankAccount?.owner_data
      ? `${initialBankAccount.owner_data?.doc_type}${saparateDocSelected}${initialBankAccount.owner_data?.doc_number}`
      : ''
  );

  const handleOwnerDataChange = (e) => {
    setChange(true);
    if (e.target.name === 'docSelected') {
      setDocSelected(e.target.value);
      setBankAccount({
        ...bankAccount,
        owner_data: {
          ...bankAccount.owner_data,
          doc_number: e.target.value.split(saparateDocSelected)[1],
          doc_type: e.target.value.split(saparateDocSelected)[0],
          name:
            e.target.value.split(saparateDocSelected)[0] === 'NIT'
              ? business_name
              : `${legal_agent.first_name} ${legal_agent.last_name}`,
        },
      });
    } else {
      setBankAccount({
        ...bankAccount,
        owner_data: {
          ...bankAccount.owner_data,
          [e.target.name]: e.target.value,
        },
      });
    }
  };

  const handleAccountDataChange = (e) => {
    setChange(true);
    const accountTypeObject = filter(bankAccountTypeColombia, ['value', e.target.value]);

    setBankAccount({
      ...bankAccount,
      account_data: {
        ...bankAccount.account_data,
        account_type_description: accountTypeObject[0].text,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleAccountNumberChange = (e) => {
    setChange(true);
    setBankAccount({
      ...bankAccount,
      account_data: {
        ...bankAccount.account_data,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleBankDataChange = (e) => {
    setChange(true);
    const bankObject = filter(banksCodes, ['code', e.target.value]);

    setBankAccount({
      ...bankAccount,
      bank_data: {
        ...bankAccount.bank_data,
        finantial_institution_name: bankObject[0].bank,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleCancel = () => {
    history.goBack();
  };

  const setButtonEnabled =
    !docSelected ||
    !bankAccount.account_data?.account_type_code ||
    !bankAccount.account_data?.account_number;

  const banksToList = Object.values(banksCodes);

  const Banks = banksToList && banksToList?.map((item) => ({ value: item.code, text: item.bank }));

  const handleSubmit = () => {
    if (edit) {
      setModal({
        body: (
          <ConfirmationModal
            title={t('bankAccount.editBankAccount.modalConfirm.title')}
            handleConfirm={() => {
              updateBankAccount(bankAccount);
            }}
          />
        ),
      });
    } else {
      createBankAccount(bankAccount);
    }
  };
  return (
    <>
      {
        /** TODO: motivo de requerir docs no vacio
         * En edicion el campo tiene valor previamente cargado y no machea con opciones en @param props.docs
         * props.docs inicialmente es un array vacio por lo que provoca que se rompa el <Select /> de material-ui.
         */
        docs.length > 0 && (
          <Card className={['popup_card', classes.card].join(' ')}>
            {edit ? (
              <h3>{t('bankAccount.editBankAccount.subtitle')}</h3>
            ) : (
              <h3>{t('paymentsFoundsOut.common.bankAccount')}</h3>
            )}
            <TextNote>{t('paymentsFoundsOut.common.description')}</TextNote>
            <GenericForm
              data-testid="formbankaccount-form"
              justify="flex-end"
              schema={schemaCo}
              item
              xs={12}
              onSubmit={handleSubmit}
              container
              spacing={3}
            >
              <Select
                fullWidth
                label={t('paymentsFoundsOut.common.document')}
                value={docSelected}
                id="doc_number"
                placeholder={capitalize('paymentsFoundsOut.common.document')}
                className={classes.select}
                variant="outlined"
                handleMyChange={handleOwnerDataChange}
                options={docs}
                gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
                name="docSelected"
              />
              <InputText
                value={bankAccount?.owner_data?.name && bankAccount?.owner_data?.name}
                id="name"
                name="name"
                defaultValue={t('paymentsFoundsOut.common.nameOrBussinesName')}
                className={classes.input}
                variant="outlined"
                gridProps={{ xs: 12, item: true, className: classes.gridInput }}
                disabled
                label={
                  bankAccount?.owner_data?.doc_type
                    ? bankAccount?.owner_data?.doc_type === 'NIT'
                      ? t('common.legalEntity')
                      : t('common.name')
                    : ''
                }
              />
              <Select
                fullWidth
                label={t('bankAccount.bank')}
                value={bankAccount.bank_data?.finantial_institution_id}
                id="bank_name"
                className={classes.select}
                variant="outlined"
                handleMyChange={handleBankDataChange}
                options={Banks}
                name="finantial_institution_id"
                gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
              />
              <InputText
                label={t('bankAccount.bankNumber')}
                handleChange={handleAccountNumberChange}
                value={bankAccount.account_data?.account_number}
                id="account_number"
                name="account_number"
                className={classes.input}
                variant="outlined"
                gridProps={{ xs: 12, item: true, className: classes.gridInput }}
                inputProps={{
                  minLength: 4,
                  maxLength: 16,
                  title: '',
                }}
              />
              <Select
                fullWidth
                label={t('bankAccount.bankType')}
                value={bankAccount.account_data?.account_type_code}
                id="account_type_code"
                placeholder={capitalize('paymentsFoundsOut.common.document')}
                className={classes.select}
                variant="outlined"
                handleMyChange={handleAccountDataChange}
                options={bankAccountTypeColombia}
                name="account_type_code"
                gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
              />
              {edit ? (
                <Grid item xs={12} className={classes.formButtonContainer} gridProps={{ xs: 12 }}>
                  <Button
                    id="cancel-edit-bank-account"
                    onClick={handleCancel}
                    variant="outlined"
                    data-testid="formbankaccount-cancel-button"
                  >
                    {t('bankAccount.editBankAccount.cancel')}
                  </Button>
                  <Button
                    id="confirm-edit-bank-account"
                    type="submit"
                    data-testid="formbankaccount-save-button"
                    disabled={!anyChange || setButtonEnabled}
                  >
                    {t('bankAccount.editBankAccount.save')}
                  </Button>
                </Grid>
              ) : (
                <Grid item xs={12} className={classes.formButtonContainer} gridProps={{ xs: 12 }}>
                  <Button
                    onClick={handleCancel}
                    id="cancel-bank-account"
                    variant="outlined"
                    data-testid="formbankaccount-cancel-button"
                  >
                    {t('bankAccount.editBankAccount.cancel')}
                  </Button>
                  <Button
                    cardButton
                    id="confirm-bank-account"
                    type="submit"
                    data-testid="formbankaccount-add-button"
                    disabled={setButtonEnabled}
                  >
                    {t('paymentsFoundsOut.common.add')}
                  </Button>
                </Grid>
              )}
            </GenericForm>
          </Card>
        )
      }
    </>
  );
};

FormBankAccountCo.propTypes = {
  edit: PropTypes.bool,
  docs: PropTypes.array.isRequired,
};

FormBankAccountCo.defaultProps = {
  edit: false,
};

export default FormBankAccountCo;
